import React, { useCallback, useEffect, useRef } from "react";
import { FormHelperText, Typography } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import { usePortServicesFlow } from "../context/PortServicesFlowContext";

const LANG_BASE = "vessel-call.port-services";
const BILL_OF_LADING_UPLOAD_TRANSACTION_CONTEXT = {
  module: "vessel_call",
  screen: "Port Service Bill Of Lading",
  action: "upload_bill_of_lading_attachment",
  entityType: "document",
};

const BillOfLadingAttachment = () => {
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const { t } = useTranslation();
  const { errors, setErrors, setBillOfLading } = usePortServicesFlow();

  const billOfLadingAttachmentUploadSignatureRef = useRef<string | null>(null);
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const billOfLadingAttachment = useUploadStore(
    (s) => s.filesByKey.billOfLadingAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );

  const fileProfiles = useFileUploadProfiles();
  const internalProfile = fileProfiles.portServicesRequestFile;
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const setBillOfLadingAttachmentFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("billOfLadingAttachment");
        return;
      }
      setFilesForKey("billOfLadingAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const removeBillOfLadingAttachment = () => {
    clearFilesForKey("billOfLadingAttachment");
    setBillOfLading({ documentUrl: "", documentName: "", fileType: "" });
  };

  const documentUploadMutation = useMutation({
    mutationFn: async ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(BILL_OF_LADING_UPLOAD_TRANSACTION_CONTEXT, async (transactionId) => {
        const payloadForSasToken = {
          prefix: SasTokenPrefix.PORT_SERVICES,
          contentType: file.type || null,
          privilege: SasTokenPrivilege.WRITE,
          blobUrl: null,
        };

        const { blobUrl, sasToken } = await getSasToken(
          payloadForSasToken,
          ...(transactionId ? [transactionId] : []),
        );
        const [uploadResult] = await uploadFileToAzureBlob({
          file,
          blobUrl,
          sasToken,
          certificateType: documentType,
        });

        const documentUrl = uploadResult?.documentUrl ?? blobUrl;
        setBillOfLading({
          documentUrl: documentUrl,
          documentName: file?.name,
          fileType: file?.type,
        });
        if (errors.billOfLading) {
          setErrors({ ...errors, billOfLading: undefined });
        }
        return { documentUrl, documentType };
      }),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate({ file, documentType });
  };

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (value: { billOfLadingAttachment?: string; files?: string }) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ billOfLadingAttachment: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors({ files: undefined });
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  useEffect(() => {
    return () => {
      billOfLadingAttachmentUploadSignatureRef.current = null;
      removeBillOfLadingAttachment();
    };
  }, []);

  return (
    <div className="flex flex-col gap-2 mb-4" data-testid="port-services-bill-of-lading-attachment">
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t(`${LANG_BASE}.fields.bill-of-lading`)}
      </Typography>
      <DocumentUpload
        testId="port-services-bill-of-lading-attachment-upload"
        onUpload={(newFiles) =>
          handleFileUpload({
            newFiles,
            setUploadErrors: (fileErrors) =>
              setErrors({
                ...errors,
                ...fileErrors,
              }),
            setUploadFiles: setBillOfLadingAttachmentFiles,
            onUploadDocument: (file) => handleDocumentUpload(file, "BILL_OF_LADING_DOCUMENT"),
            uploadTracker: billOfLadingAttachmentUploadSignatureRef,
          })
        }
        name="billOfLadingAttachment"
        conditions={internalProfile}
        files={billOfLadingAttachment}
        setFiles={(next) => setFilesForKey("billOfLadingAttachment", next)}
        maxFiles={1}
        allowedExtensions={allowedInternalExtensions}
        uploadDescription={t("upload.pdf-only")}
      />
      {errors.billOfLadingAttachment && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>
          {errors.billOfLadingAttachment}
        </FormHelperText>
      )}
      {errors.billOfLading && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>{errors.billOfLading}</FormHelperText>
      )}
      {billOfLadingAttachment[0] && (
        <SingleFile
          file={billOfLadingAttachment[0]}
          testId="port-services-bill-of-lading-attachment"
          fieldName="billOfLadingAttachment"
          currentFileIndex={0}
          setFiles={() => {
            setErrors({ ...errors, files: undefined });
            setBillOfLadingAttachmentFiles([]);
            billOfLadingAttachmentUploadSignatureRef.current = null;
          }}
          removeFn={removeBillOfLadingAttachment}
        />
      )}
    </div>
  );
};

export default BillOfLadingAttachment;
